import { ApiRequest } from './network';
import { SparrowApi } from 'api/network/adapters/SparrowApi';
import { AxiosPromise } from 'axios';
import { TranslatableNewsletterContent, TranslatedNewsletterContent } from 'modules/common/components/authoring/models';
import { TranslatedContentType } from 'modules/common/hooks/useTranslatedContent';
import { TranslationContentType, TranslationTextFormat } from 'modules/common/models';
import { IEmailTranslatableContent, IEmailTranslatableContentByLcid } from 'modules/emails/models';
import { ILcidTranslateDetails } from 'modules/localization/models';
import { TranslatablePostContent } from 'modules/posts/models';
import { IReadabilitySettings, IReadabilitySettingsOptionModel } from 'modules/settings';
import { IGenerateContentRequest, IGenerateContentResponse, ISuggestion } from 'modules/smartContent/models';

interface ITranslateRequest {
    toLcid: string;
    fromLcid: string;
    text: string;
    contentType: TranslationContentType;
    textFormat: TranslationTextFormat;
}

interface ITranslatorTextResult {
    Translations: ITranslation[];
}

interface ITranslation {
    text: string;
}

interface ICognitiveServicesApi {
    getSuggestions(): ApiRequest<ISuggestion[]>;
    getReadabilityOptions(): ApiRequest<IReadabilitySettingsOptionModel>;
    getReadabilitySettings(): ApiRequest<IReadabilitySettings>;
    generateContent(req: IGenerateContentRequest): AxiosPromise<IGenerateContentResponse>;
    saveReadabilitySettings(settings: IReadabilitySettings): ApiRequest<IReadabilitySettings>;
    translateText(req: ITranslateRequest): AxiosPromise<ITranslatorTextResult>;
}

export class CognitiveServicesApi implements ICognitiveServicesApi {
    constructor(private _sparrowApi: SparrowApi) { }

    /**
     * Generate content via GPT
     * @param req
     * @returns ApiReqeust<IGenerateContentResponse>
     */
    public generateContent = (req: IGenerateContentRequest): AxiosPromise<IGenerateContentResponse> => {
        return new ApiRequest(
            this._sparrowApi.TenantRequest().post("{tenant}/adminapi/v1/cognitiveservices/generateContent?platform=web", req)
        )
        .catch((err) => {
            throw err;
        });
    }

    /**
     * Get tenant suggestions for prompts to GPT
     * @returns ApiRequest<ISuggestion[]>
     */
    public getSuggestions = (): ApiRequest<ISuggestion[]> => {
        return new ApiRequest(
            this._sparrowApi.TenantRequest().get(`{tenant}/adminapi/v1/cognitiveservices/suggestions`)
        );
    }

    /**
     * Get tenant's current readability settings
     * @returns ApiRequest<IReadabilitySettings>
     */
    public getReadabilitySettings(): ApiRequest<IReadabilitySettings> {
        return new ApiRequest(
            this._sparrowApi.TenantRequest().get(`{tenant}/adminapi/v1/cognitiveservices/readabilitysettings`)
        );
    }

    /**
     * Get the options for readability settings
     * @returns ApiRequest<IReadabilitySettingsOptionModel>
     */
    public getReadabilityOptions(): ApiRequest<IReadabilitySettingsOptionModel> {
        return new ApiRequest(
            this._sparrowApi.TenantRequest().get(`{tenant}/adminapi/v1/cognitiveservices/readabilitysettings/options`)
        );
    }

    /**
     * Save readability settings
     * @param settings
     * @returns ApiRequest<IReadabilitySettings>
     */
    public saveReadabilitySettings(settings: IReadabilitySettings): ApiRequest<IReadabilitySettings> {
        return new ApiRequest(
            this._sparrowApi.TenantRequest().post(`{tenant}/adminapi/v1/cognitiveservices/readabilitysettings`, settings)
        );
    }

    public translateText(translateReq: ITranslateRequest): AxiosPromise<ITranslatorTextResult> {
        return new ApiRequest(
            this._sparrowApi.TenantRequest().post(`{tenant}/adminapi/v1/cognitiveservices/translate`, translateReq)
        ).catch((err) => {
            throw err;
        });
    }

    public translateAllPostEventLanguages = async (id: string, content: TranslatablePostContent, outputLanguages: ILcidTranslateDetails[]): Promise<TranslatedContentType<TranslatablePostContent>[]> => {
        return await new ApiRequest(
            this._sparrowApi.TenantRequest().post(`{tenant}/api/v1/cognitiveservices/translatortext/translateall/${id}?type=post`, { sentTranslation: content, outputLanguages })
        )
        .then((response: TranslatedContentType<TranslatablePostContent>[]) => response )
        .catch((err) => {
            throw err;
        });
    }

    public async translateAllEmailLanguages(input: IEmailTranslatableContent, outputLanguages: ILcidTranslateDetails[]): Promise<IEmailTranslatableContentByLcid[]> {
        return await new ApiRequest(
            this._sparrowApi.TenantRequest().post(`{tenant}/api/v1/cognitiveservices/translatortext/translateall/email`, { emailTranslation: input, outputLanguages })
        )
        .then((response: IEmailTranslatableContentByLcid[]) => { return response })
        .catch((err) => {
            throw err;
        });
    }

    public async translateAllNewsletterLanguages(content: TranslatableNewsletterContent, outputLanguages: ILcidTranslateDetails[]): Promise<TranslatedNewsletterContent[]> {
        return await new ApiRequest(
            this._sparrowApi.TenantRequest().post(`{tenant}/api/v1/cognitiveservices/translatortext/translateall/newsletter`, { newsletterTranslation: content, outputLanguages })
        )
        .then((response: TranslatedNewsletterContent[]) => { return response })
        .catch((err) => {
            throw err;
        });
    }
}
